import { observable, action, runInAction, computed } from "mobx";
import { Subscription } from "rxjs";

import {
	getUserFromLocalStorage,
	getCurrentLanguage,
	getUserToken,
	ISOToDisplayCode,
	getWalletByShopIdAndAccountTypeId,
	getShopId,
	getAgencyId,
} from "@utils";

import { IBetSlipContainer, CashoutType } from "@gp/hub";

import { localizationService } from "@state";

import { DocumentExpirationErrorCodes } from "@api-types/documentExpiration/documentExpiration";
import { BetSlipInteractionTypeEnum } from "@data-types/analytics-tracker/BetSlipInteractionTypeEnum";
import { DateTime } from "luxon";
import ModalService from "@services/modals/ModalService";
import { AnnualIncomeErrorCodes } from "@api-types/user/AnnualIncomeErrorCodes";
import { LoaderStore } from "@state/stores/common";
import { checkDecimals } from "@utils/my-bets/helpers";
import { AutocashoutApiService } from "@api-services/agency/AutocashoutApiService";
import BetSlipApiService from "@api-services/agency/BetSlipApiService";
import { Bet } from "@models";
import { CashoutLocation } from "@utils/common/cashoutLocation";
import { BetSlipCashoutHistory } from "@api-types/agency/autocashout/BetSlipCashoutHistory";
import { BetSlipSecureWinSetting } from "@api-types/agency";
import { OpenCashoutWithQrCodeModalType } from "@data-types/my-bets/OpenCashoutWithQrCodeModal";
import { OpenCashoutModalType } from "@data-types/my-bets/OpenCashoutModal";
import { BetSlipErrorCodesEnum } from "@state/bet-slip/BetSlipErrorCodesEnum";
import { ICashoutStoreParent } from "./ICashoutStoreParent";
import { THEME_SETTINGS } from "@theme-settings";
import { analyticsTracker } from "@utils/analytics";
import MyBetsStore from "./MyBetsStore";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { RequestContextType } from "@utils/api-config/RequestContext";

export class CashoutStore {
	public isStoreInitialized: boolean = false;

	private parentStore: ICashoutStoreParent;
	private loader: LoaderStore;
	private subscriptionIds: { id: string; subscription: Subscription }[] = [];
	private refreshDebounceId: ReturnType<typeof setTimeout> | undefined;
	private countdownInterval: ReturnType<typeof setTimeout> | undefined;

	private _timeoutCache = new Map();
	private _myBetsTimeoutCache = new Map();

	private _currentCashoutValue: number | null = null;
	private _minSecureWinThresholdIncreasePercentage = 0.15;

	constructor(parentStore: ICashoutStoreParent) {
		this.parentStore = parentStore;

		this.loader = new LoaderStore();
		this.updateAutoSellValues = this.updateAutoSellValues.bind(this);
		this.isCashedOut = this.isCashedOut.bind(this);
	}

	//#region  observable

	@observable isCashoutModalOpen: boolean = false;
	@observable isEventCashoutModalOpen: boolean = false;
	@observable cashoutModalData: OpenCashoutModalType | null;
	@observable eventCashoutModalData: OpenCashoutModalType | null;
	@observable isCashoutFormSubmitting: boolean = false;
	@observable isCashoutWithQrCodeModalOpen: boolean = false;
	@observable
	cashoutWithQrCodeModalData: OpenCashoutWithQrCodeModalType | null;
	@observable isAnonymousCashoutWithQrCodeSuccessModalOpen: boolean = false;

	@observable countdownCounter: number = 0;
	@observable isCountdownVisible: boolean = false;

	@observable cashoutHistory: BetSlipCashoutHistory | null = null;
	@observable cashoutHistorySlipId: string | null = null;
	@observable isCashoutHistoryOpen: boolean = false;

	@observable isAutosellModalOpen: boolean = false;
	@observable isEventAutosellModalOpen: boolean = false;
	@observable isSaveBtnDisabled: boolean = true;
	@observable isConfigurationExists: boolean = false;
	@observable secureWinAmount: string | null = null;
	@observable minThreshold: string | null = null;
	@observable maxThreshold: string | null = null;
	@observable openedAutosellModalBetSlipId: string | null = null;
	@observable isAutocashoutUnavailable: boolean = false;
	@observable isAutosellFormValid: boolean = true;
	@observable isMinThresholdValid: boolean = true;
	@observable isMaxThresholdValid: boolean = true;
	@observable isAutosellModalCashedOut: boolean = false;
	@observable autosellModalCashoutGain = null;
	@observable bet: Bet | null = null;
	@observable cashoutLocation:
		CashoutLocation.MY_BETS | CashoutLocation.OFFER | null = null;
	@observable cashoutEventId: string | null = null;

	//#endregion observable

	@computed public get isLoading() {
		return this.loader.isLoading;
	}

	//#region cashout subscription

	@action.bound
	async initializeCashoutSubscription(
		eventId: string | null = null,
		betsList: Bet[] | null = null
	) {
		this.disposeSubscription(eventId);
		const submittedSlips = (betsList || this.parentStore.betsList).filter(
			(b) => b.betSlipStatus.abrv === "submitted"
		);

		if (submittedSlips.length === 0) {
			return;
		}

		let userAccount = getWalletByShopIdAndAccountTypeId(
			submittedSlips[0].shopId,
			submittedSlips[0].bettingAccountTypeId,
			this.parentStore.rootStore.userAuthStore.userAccounts || []
		);

		const slips = submittedSlips.map((bet) => ({
			betSlipId: bet.id,
			betSlipNumericalId: bet.betSlipNumber,
			isMultiOffer: bet.hasBetBuilder,
		}));

		const subscriptionFilter = {
			subscriptionId: `${getUserFromLocalStorage()?.id}${
				eventId != null ? `-${eventId}` : ""
			}-my-bets-cashout`,
			environment: WEBPACK_ENVIRONMENT_KEY,
			language: getCurrentLanguage(),
			token: getUserToken()?.token || "",
			shopId: userAccount?.shopId || getShopId(),
			agencyId: userAccount?.agencyId || getAgencyId(),
			isAgencyUser: true,
			monitoredBetSlips: slips,
		};

		const subscription = this.parentStore.rootStore.cashoutHubStore.hub
			?.getSubscription(subscriptionFilter)
			.subscribe((response) => {
				runInAction(() => {
					if (eventId == null) {
						this.parentStore.betsList.forEach((betSlip: Bet) => {
							const update = response.value.find(
								(u) => u.id === betSlip.id
							);

							if (update && !betSlip.isRemoved) {
								this.updateSlipCashout(betSlip, update);

								if (
									(this.isAutosellModalOpen ||
										this.isEventAutosellModalOpen) &&
									this.openedAutosellModalBetSlipId &&
									this.openedAutosellModalBetSlipId ===
										betSlip.id
								) {
									const newCashoutValue = update.value;

									if (this.isCashedOut(update)) {
										this.isAutosellModalCashedOut = true;
										this.autosellModalCashoutGain =
											//@ts-expect-error
											update.cashoutGain;
									} else if (
										this._currentCashoutValue !==
											newCashoutValue &&
										newCashoutValue &&
										update.maxGain
									) {
										this.updateAutoSellValues(
											newCashoutValue,
											update.maxGain
										);
									}
								}
							}

							if (update != null && this.isCashedOut(update)) {
								this.onCashoutSync(update.id);
							}
						});
					} else {
						if (
							this.parentStore.betsPerEvent != null &&
							this.parentStore.betsPerEvent.length > 0
						) {
							const eventIdx =
								this.parentStore.betsPerEvent.findIndex(
									(e) => e.eventId === eventId
								);

							if (eventIdx > -1) {
								response.value.forEach((betSlip) => {
									if (this.parentStore.betsPerEvent != null) {
										const betPerEvent =
											this.parentStore.betsPerEvent[
												eventIdx
											].bets.find(
												(b) => b.id === betSlip.id
											);
										if (betPerEvent != null) {
											this.updateSlipCashout(
												betPerEvent,
												betSlip
											);
										}
									}
								});
							}
						}
					}
				});
			});

		if (this.parentStore.rootStore.cashoutHubStore.hub) {
			this.subscriptionIds?.push({
				id: subscriptionFilter.subscriptionId,
				subscription: subscription,
			});
		}
	}

	@action.bound
	updateSlipCashout(betSlip: Bet, update: Partial<IBetSlipContainer>) {
		this.removeBetSlipIdFromTimeOutCache(betSlip);
		this.updateBetSlipIndicator(betSlip, update);

		// when betslip is auto-cashed out and should be removed after 10 seconds
		if (
			!this._myBetsTimeoutCache.has(update.id) &&
			this.isCashedOut(update)
		) {
			betSlip.betStatus = {
				abrv: "won",
				name: "WON",
			};
			betSlip.indicator = 0;
			betSlip.displayedAmount = null; // remove displayed amount to hide button
			betSlip.isCancellationAvailable = false;

			for (let offer of betSlip.betSlipOffers) {
				offer.bettingOfferStatus = 9;
			}

			const timeoutId = setTimeout(() => {
				this._myBetsTimeoutCache.delete(update.id);
				runInAction(() => {
					betSlip.isRemoved = true;
				});
			}, 10000);

			this._myBetsTimeoutCache.set(update.id, timeoutId);

			this.parentStore.rootStore.userAuthStore.refreshUserBalance();

			return;
		}

		betSlip.displayedAmount =
			!update.isPaidOut && update.value != null
				? update.value.toFixed(2)
				: null;
		betSlip.cType = update.cType;
		betSlip.calculatedCashoutAmount = update.value || 0;
	}

	@action.bound
	private removeBetSlipIdFromTimeOutCache(betSlip: Bet) {
		if (this._timeoutCache.has(betSlip.id)) {
			clearTimeout(this._timeoutCache.get(betSlip.id));
			this._timeoutCache.delete(betSlip.id);
		}
	}

	@action.bound
	updateBetSlipIndicator(betSlip: Bet, update: Partial<IBetSlipContainer>) {
		const currAmount = Number(betSlip.displayedAmount);

		// initial value for currAmount will be 0 so we must skip adding indicators
		if (currAmount <= 0 || update.value === currAmount) {
			betSlip.indicator = 0;
			return;
		}

		if (update.value != null && update.value > currAmount) {
			betSlip.indicator = 1;
		} else if (update.value != null && update.value < currAmount) {
			betSlip.indicator = -1;
		}

		const tId = setTimeout(() => {
			this._timeoutCache.delete(betSlip.id);
			runInAction(() => {
				betSlip.indicator = 0;
			});
		}, 3500);

		this._timeoutCache.set(betSlip.id, tId);
	}

	//#endregion cashout subscription

	//#region actions

	@action.bound
	async onInitialize() {
		try {
			runInAction(() => {
				this.isStoreInitialized = true;
			});
		} catch (error) {
			console.error(error);
		}
	}

	@action.bound
	async cashout(
		{
			id,
			calculatedCashoutAmount,
			cType,
			isChangeAccepted,
			userId,
			liveBetSlipCountdown,
		}: Omit<OpenCashoutModalType, "isChangeAccepted"> & {
			isChangeAccepted: boolean;
		},
		cashoutLocation: CashoutLocation.MY_BETS | CashoutLocation.OFFER | null
	) {
		if (!this.parentStore.rootStore.userAuthStore.user) {
			return;
		}
		this.isCashoutFormSubmitting = true;
		this.cashoutLocation = cashoutLocation;

		if (liveBetSlipCountdown) {
			this.startCountdown(liveBetSlipCountdown);
		}

		analyticsTracker.trackCashOutRequested(
			cashoutLocation,
			calculatedCashoutAmount
		);

		try {
			this.closeCashoutModal();
			this.closeEventCashoutModal();
			this.closeBetDetailsCashout();
			let user = this.parentStore.rootStore.userAuthStore.user;
			if (
				user?.id != userId &&
				this.parentStore.rootStore.userAuthStore.secondaryUser
			) {
				user = this.parentStore.rootStore.userAuthStore.secondaryUser;
			}

			const response = await BetSlipApiService.cashout(
				id,
				calculatedCashoutAmount,
				cType,
				isChangeAccepted,
				createApiRequestOptions(user.userContextType)
			);

			const betSlip = this.parentStore.betsList.find(
				(betSlip: Bet) => betSlip.id === id
			);

			if (betSlip != null) {
				this.updateSlipCashout(betSlip, { isPaidOut: true });
			}

			if (
				this.parentStore.betsPerEvent != null &&
				this.parentStore.betsPerEvent.length !== 0
			) {
				this.parentStore.betsPerEvent.forEach((eventBets) => {
					const betPerEvent = eventBets.bets.find((b) => b.id === id);
					if (betPerEvent != null) {
						this.updateSlipCashout(betPerEvent, {
							isPaidOut: true,
						});
					}
				});
			}

			let gainValue = response.betSlip.payout ?? response.betSlip.gain;

			if (response.betSlipProcessingResponse) {
				this.parentStore.rootStore.notificationStore.success(
					response.betSlipProcessingResponse.errorMessage
				);
			} else {
				this.parentStore.rootStore.notificationStore.success(
					localizationService.t("mybets:CASHOUT.SUCCESS_MESSAGE", {
						amount: gainValue.toFixed(2),
						currency: ISOToDisplayCode(
							THEME_SETTINGS.defaultCurrency
						),
					})
				);
			}

			this.parentStore.rootStore.userAuthStore.resolveBalance();
			analyticsTracker.trackBetSlipInteraction(
				BetSlipInteractionTypeEnum.Cashout
			);
		} catch (err) {
			console.log(err);

			let errorMessage = localizationService.t(
				"mybets:CASHOUT.FAILURE_MESSAGE"
			);

			if (err.data == null) {
				this.parentStore.rootStore.notificationStore.error(
					errorMessage
				);
			} else {
				let error = err.data;
				if (error.betSlipProcessingResponse != null) {
					errorMessage = error.betSlipProcessingResponse.errorMessage;
				} else if (error.validationResponse != null) {
					errorMessage = error.validationResponse.errorMessage;
				}

				errorMessage = errorMessage.replaceAll(
					"{currency}",
					ISOToDisplayCode(THEME_SETTINGS.defaultCurrency)
				);

				const errorCode = error?.betSlipProcessingResponse?.errorCode;
				if (errorCode === 400082) {
					this.parentStore.rootStore.notificationStore.error(
						error.betSlipProcessingResponse.errorMessage
					);
				} else if (errorCode === 400262) {
					error.betSlipProcessingResponse.errorMessage =
						error.betSlipProcessingResponse.errorMessage.replace(
							"{0}",
							DateTime.fromISO(
								error.betSlipProcessingResponse
									.previouslyClosedAt
							).toFormat("f")
						);
					error.betSlipProcessingResponse.errorMessage =
						error.betSlipProcessingResponse.errorMessage.replace(
							"{1}",
							DateTime.fromISO(
								error.betSlipProcessingResponse.nextOpening
							).toFormat("f")
						);

					this.parentStore.rootStore.notificationStore.error(
						error.betSlipProcessingResponse.errorMessage
					);
				} else if (errorCode === 400083) {
					const id = error.betSlip.id;
					const calculatedCashoutAmount =
						error.betSlip.calculatedCashoutAmount;
					const displayedAmount =
						error.betSlip.calculatedCashoutAmount.toFixed(2);
					const isLive = error.betSlip.isLive;
					const message = errorMessage;

					if (this.cashoutLocation == CashoutLocation.OFFER) {
						this.openEventCashoutModal(
							{
								userId,
								id,
								calculatedCashoutAmount,
								displayedAmount,
								cType,
								isLive,
								message,
								liveBetSlipCountdown,
							},
							""
						);
					} else {
						this.openCashoutModal({
							userId,
							id,
							calculatedCashoutAmount,
							displayedAmount,
							cType,
							isLive,
							message,
							liveBetSlipCountdown,
						});
					}
				} else if (
					errorCode ===
					BetSlipErrorCodesEnum.WalletActivationGracePeriod
				) {
					this.parentStore.rootStore.walletGracePeriodPopupStore.setMessage(
						errorMessage
					);
					this.parentStore.rootStore.walletGracePeriodPopupStore.showPopup();
				} else if (
					errorCode ===
						DocumentExpirationErrorCodes.UserDocumentExpirationDateExpired ||
					errorCode ===
						DocumentExpirationErrorCodes.UserDocumentExpirationDateNotSet
				) {
					this.parentStore.rootStore.accountVerificationStore.showPopup(
						"account-settings/document-expiration",
						"DOCUMENT_EXPIRATION.TITLE",
						errorMessage
					);
				} else if (
					errorCode ===
					AnnualIncomeErrorCodes.AnnualIncomeUpdateRequired
				) {
					this.parentStore.rootStore.accountVerificationStore.showPopup(
						"account-settings/personal-data",
						"COMMON.NAVIGATION.PERSONAL_DATA",
						errorMessage
					);
				} else if (
					errorCode ===
						BetSlipErrorCodesEnum.BetSlipCashoutLUGASUnavailable ||
					errorCode ===
						BetSlipErrorCodesEnum.BetSlipCashoutOASISUnavailable
				) {
					this.parentStore.rootStore.notificationStore.error(
						error.betSlipProcessingResponse.errorMessage
					);
				} else if (
					errorCode == BetSlipErrorCodesEnum.BetSlipActionsDisabled
				) {
					{
						this.parentStore.rootStore.notificationStore.error(
							errorMessage
						);
					}
				} else {
					this.parentStore.rootStore.notificationStore.error(
						localizationService.t("mybets:CASHOUT.FAILURE_MESSAGE")
					);
				}
			}
		} finally {
			this.stopCountDown();

			await this.onCashoutSync(id);

			runInAction(() => {
				this.isCashoutFormSubmitting = false;
				this.cashoutEventId = null;
			});
		}
	}

	@action.bound
	async refresh() {
		clearTimeout(this.refreshDebounceId);
		this.refreshDebounceId = setTimeout(async () => {
			this.parentStore.refresh();
		}, 500);
	}

	@action.bound
	async onCashoutSync(betId: string) {
		const betsListBet = this.parentStore.betsList.find(
			(item) => item.id === betId
		);
		const offerBetsList = this.parentStore.betsPerEvent?.filter(
			(item) => item.bets.find((bet) => bet.id === betId) != null
		);

		if (betsListBet != null) {
			this.parentStore.refresh();
		}

		if (offerBetsList != null && offerBetsList.length > 0) {
			offerBetsList.forEach((item) => {
				if (this.parentStore.fetchAdditionalOffers != null) {
					if (this.parentStore.betsPerEvent != null) {
						const currentEventIdx =
							this.parentStore.betsPerEvent.findIndex(
								(eb) => eb.eventId === item.eventId
							);

						if (currentEventIdx > -1) {
							this.parentStore.betsPerEvent[
								currentEventIdx
							].pageNumber = 1;
						}

						this.parentStore.fetchAdditionalOffers(item.eventId);
					}
				}
			});
		}
	}

	@action.bound
	stopCountDown() {
		this.countdownCounter = 0;
		clearInterval(this.countdownInterval);
		this.isCountdownVisible = false;
	}

	@action.bound
	startCountdown(liveBetSlipCountdown: number) {
		this.countdownCounter = liveBetSlipCountdown;

		if (this.countdownCounter !== 0) {
			this.isCountdownVisible = true;
			this.countdownInterval = setInterval(async () => {
				--this.countdownCounter;
				if (this.countdownCounter <= 0) {
					clearInterval(this.countdownInterval);
				}
			}, 1000);
		}
	}

	@action.bound
	closeCashoutModal() {
		this.isCashoutModalOpen = false;
		this.cashoutModalData = null;
	}

	@action.bound
	closeEventCashoutModal() {
		this.isEventCashoutModalOpen = false;
		this.eventCashoutModalData = null;
	}

	@action.bound
	closeBetDetailsCashout() {
		this.cashoutModalData = null;
	}

	@action.bound
	openCashoutModal(modalData: OpenCashoutModalType) {
		this.isCashoutModalOpen = true;
		this.cashoutModalData = modalData;
		ModalService.onOpen();
	}

	@action.bound
	openBetDetailsCashout(modalData: OpenCashoutModalType) {
		this.cashoutModalData = modalData;
	}

	@action.bound
	openEventCashoutModal(modalData: OpenCashoutModalType, eventId: string) {
		this.isEventCashoutModalOpen = true;
		this.cashoutEventId = eventId;
		this.eventCashoutModalData = modalData;
		this.cashoutLocation = CashoutLocation.OFFER;
	}

	//#endregion actions

	//#region cashout with QR code
	@action.bound
	async cashoutWithQrCode({
		id,
		calculatedCashoutAmount,
		cType,
		isChangeAccepted,
		code,
		betSlipNumber,
		userId,
		owner,
		liveBetSlipCountdown,
	}: Omit<OpenCashoutWithQrCodeModalType, "isChangeAccepted"> & {
		isChangeAccepted: boolean;
	}) {
		this.isCashoutFormSubmitting = true;

		if (liveBetSlipCountdown) {
			this.startCountdown(liveBetSlipCountdown);
		}

		try {
			this.closeCashoutWithQrCodeModal();

			let user = this.parentStore.rootStore.userAuthStore.user;

			if (
				user?.id != userId &&
				this.parentStore.rootStore.userAuthStore.secondaryUser
			) {
				user = this.parentStore.rootStore.userAuthStore.secondaryUser;
			}
			const response = await BetSlipApiService.cashoutWithQrCode(
				id,
				calculatedCashoutAmount,
				isChangeAccepted,
				code,
				betSlipNumber,
				createApiRequestOptions(user?.userContextType)
			);

			const betSlip = this.parentStore.betsList.find(
				(betSlip) => betSlip.id === id
			);

			if (betSlip != null) {
				this.updateSlipCashout(betSlip, { isPaidOut: true });
			}

			if (
				this.parentStore.betsPerEvent != null &&
				this.parentStore.betsPerEvent.length !== 0
			) {
				this.parentStore.betsPerEvent.forEach((eventBets) => {
					const betPerEvent = eventBets.bets.find(
						(b: Bet) => b.id === id
					);
					if (betPerEvent != null) {
						this.updateSlipCashout(betPerEvent, {
							isPaidOut: true,
						});
					}
				});
			}

			let gainValue = response.betSlip.payout ?? response.betSlip.gain;

			if (
				response.betSlipProcessingResponse &&
				response.betSlip.shopAddress === null
			) {
				this.parentStore.rootStore.notificationStore.success(
					response.betSlipProcessingResponse.errorMessage
				);
			} else if (
				response.betSlip !== null &&
				response.betSlip.shopAddress !== null
			) {
				const id = response.betSlip.id;
				const calculatedCashoutAmount =
					response.betSlip.calculatedCashoutAmount || 0;
				const displayedAmount = calculatedCashoutAmount.toFixed(2);
				const isLive = response.betSlip.isLive;
				const code = response.betSlip.code;
				const message =
					response.betSlipProcessingResponse != null
						? response.betSlipProcessingResponse.errorMessage
						: null;
				const shopAddress = response.betSlip.shopAddress;
				const betSlipNumber = response.betSlip.betSlipNumber;
				const userId = response.betSlip.userId;
				const owner =
					this.parentStore.rootStore.userAuthStore.getUserTypeFromBettingAccountTypeId(
						response.betSlip.bettingAccountTypeId || ""
					);

				this.openAnonymousCashoutWithQrCodeSuccessModal({
					id,
					calculatedCashoutAmount,
					displayedAmount,
					cType,
					isLive,
					code,
					message,
					shopAddress,
					betSlipNumber,
					userId,
					owner,
					liveBetSlipCountdown,
				});
			} else {
				this.parentStore.rootStore.notificationStore.success(
					localizationService.t("mybets:CASHOUT.SUCCESS_MESSAGE", {
						amount: gainValue.toFixed(2),
						currency: ISOToDisplayCode(
							THEME_SETTINGS.defaultCurrency
						),
					})
				);
			}

			if (
				getUserFromLocalStorage()?.id != undefined &&
				response.betSlip.shopAddress === null
			) {
				this.parentStore.rootStore.userAuthStore.resolveBalance();
			}
			analyticsTracker.trackBetSlipInteraction(
				BetSlipInteractionTypeEnum.Cashout
			);
		} catch (err) {
			let errorMessage = localizationService.t(
				"mybets:CASHOUT.FAILURE_MESSAGE"
			);

			if (err.data == null) {
				this.parentStore.rootStore.notificationStore.error(
					errorMessage
				);
			} else {
				let error = err.data;

				if (error.betSlipProcessingResponse != null) {
					errorMessage = error.betSlipProcessingResponse.errorMessage;
				} else if (error.validationResponse != null) {
					errorMessage = error.validationResponse.errorMessage;
				}
				errorMessage = errorMessage.replaceAll(
					"{currency}",
					ISOToDisplayCode(THEME_SETTINGS.defaultCurrency)
				);

				const errorCode = error?.betSlipProcessingResponse?.errorCode;

				if (errorCode === 400082) {
					this.parentStore.rootStore.notificationStore.error(
						error.betSlipProcessingResponse.errorMessage
					);
				} else if (errorCode === 400262) {
					error.betSlipProcessingResponse.errorMessage =
						error.betSlipProcessingResponse.errorMessage.replace(
							"{0}",
							DateTime.fromISO(
								error.betSlipProcessingResponse
									.previouslyClosedAt
							).toFormat("f")
						);
					error.betSlipProcessingResponse.errorMessage =
						error.betSlipProcessingResponse.errorMessage.replace(
							"{1}",
							DateTime.fromISO(
								error.betSlipProcessingResponse.nextOpening
							).toFormat("f")
						);
				} else if (errorCode === 400083) {
					const id = error.betSlip.id;
					const calculatedCashoutAmount =
						error.betSlip.calculatedCashoutAmount;
					const displayedAmount =
						error.betSlip.calculatedCashoutAmount.toFixed(2);
					const isLive = error.betSlip.isLive;
					const code = error.betSlip.code;
					const message = errorMessage;
					const betSlipNumber = error.betSlip.betSlipNumber;
					const userId = error.betSlip.userId;
					const owner =
						this.parentStore.rootStore.userAuthStore.getUserTypeFromBettingAccountTypeId(
							error.betSlip.bettingAccountTypeId
						);
					this.openCashoutWithQrCodeModal({
						id,
						calculatedCashoutAmount,
						displayedAmount,
						cType,
						isLive,
						code,
						message,
						betSlipNumber,
						userId,
						owner,
						liveBetSlipCountdown,
					});
				} else if (
					errorCode ===
						DocumentExpirationErrorCodes.UserDocumentExpirationDateExpired ||
					errorCode ===
						DocumentExpirationErrorCodes.UserDocumentExpirationDateNotSet
				) {
					this.parentStore.rootStore.accountVerificationStore.showPopup(
						"account-settings/document-expiration",
						"DOCUMENT_EXPIRATION.TITLE",
						errorMessage
					);
				} else if (errorCode === 400095) {
					this.parentStore.rootStore.notificationStore.error(
						error.betSlipProcessingResponse.errorMessage
					);
				} else if (errorCode === 400297) {
					this.parentStore.rootStore.notificationStore.error(
						error.betSlipProcessingResponse.errorMessage
					);
				} else if (errorCode === 400080) {
					this.parentStore.rootStore.notificationStore.error(
						error.betSlipProcessingResponse.errorMessage
					);
				} else if (
					errorCode ===
						BetSlipErrorCodesEnum.BetSlipCashoutLUGASUnavailable ||
					errorCode ===
						BetSlipErrorCodesEnum.BetSlipCashoutOASISUnavailable
				) {
					this.parentStore.rootStore.notificationStore.error(
						error.betSlipProcessingResponse.errorMessage
					);
				} else {
					this.parentStore.rootStore.notificationStore.error(
						localizationService.t("mybets:CASHOUT.FAILURE_MESSAGE")
					);
				}
			}
		} finally {
			this.stopCountDown();
			await this.refreshWithQrCode();

			runInAction(() => {
				this.isCashoutFormSubmitting = false;
			});
		}
	}

	@action.bound
	openCashoutWithQrCodeModal(modalData: OpenCashoutWithQrCodeModalType) {
		this.isCashoutWithQrCodeModalOpen = true;
		this.cashoutWithQrCodeModalData = modalData;
	}

	@action.bound
	closeCashoutWithQrCodeModal() {
		this.isCashoutWithQrCodeModalOpen = false;
		this.cashoutWithQrCodeModalData = null;
	}

	@action.bound
	closeAnonymousCashoutWithQrCodeSuccessModal() {
		this.isAnonymousCashoutWithQrCodeSuccessModalOpen = false;
		this.cashoutWithQrCodeModalData = null;
	}

	@action.bound
	openAnonymousCashoutWithQrCodeSuccessModal(
		modalData: OpenCashoutWithQrCodeModalType
	) {
		this.isAnonymousCashoutWithQrCodeSuccessModalOpen = true;
		this.cashoutWithQrCodeModalData = modalData;
	}

	@action.bound
	async refreshWithQrCode() {
		clearTimeout(this.refreshDebounceId);
		this.refreshDebounceId = setTimeout(async () => {
			this.parentStore.refreshWithQrCode?.();
		}, 500);
	}

	//#endregion cashout with QR code

	//#region cashout history

	@action.bound
	async fetchCashoutHistoryForBetSlip(bet: NonNullable<CashoutStore["bet"]>) {
		ModalService.onOpen();
		this.isCashoutHistoryOpen = true;
		this.cashoutHistorySlipId = bet.betSlipNumber;
		this.bet = bet;

		let userAccount = getWalletByShopIdAndAccountTypeId(
			bet.shopId,
			bet.bettingAccountTypeId,
			this.parentStore.rootStore.userAuthStore.userAccounts || []
		);
		let context =
			this.parentStore.rootStore.userAuthStore.mapUserIdToUserContext(
				bet.userId
			);
		if (userAccount) {
			context = createApiRequestOptions(
				RequestContextType.Wallet,
				userAccount.id
			);
		}
		try {
			const cashOutHistory = await BetSlipApiService.getCashoutHistory(
				bet.id,
				context
			);

			runInAction(() => {
				this.cashoutHistory = cashOutHistory;
			});
		} catch (error) {
			console.error(error);
			this.closeCashoutHistory();
		}
	}

	@action.bound
	closeCashoutHistory() {
		this.isCashoutHistoryOpen = false;
		this.cashoutHistory = null;
		this.bet = null;
	}

	//#endregion cashout history

	//#region disposers

	@action.bound
	disposeSubscription(eventId: string | null = null) {
		const idToUse = eventId != null ? eventId : "my-bets";

		if (this.subscriptionIds != null) {
			const subscriptionIdx = this.subscriptionIds.findIndex((subscr) =>
				subscr.id.includes(idToUse)
			);

			if (subscriptionIdx > -1) {
				this.subscriptionIds[
					subscriptionIdx
				]?.subscription?.unsubscribe?.();
				this.subscriptionIds.splice(subscriptionIdx, 1);
			}
		}
	}

	@action.bound
	disposeAllSubscriptions() {
		if (this.subscriptionIds != null && this.subscriptionIds.length > 0) {
			this.subscriptionIds.forEach((subscr) =>
				subscr?.subscription?.unsubscribe?.()
			);
			this.subscriptionIds = [];
		}
	}

	@action.bound
	onDispose() {
		this.disposeAllSubscriptions();
		this.isStoreInitialized = false;

		this.isCashoutModalOpen = false;
		this.cashoutModalData = null;
		this.isCashoutFormSubmitting = false;

		this.countdownCounter = 0;
		this.isCountdownVisible = false;

		this.cashoutHistory = null;
		this.cashoutHistorySlipId = null;
		this.isCashoutHistoryOpen = false;
	}

	//#endregion disposers

	//#region  auto sell

	@action.bound
	openAutosellModal(
		bet: {
			id: CashoutStore["openedAutosellModalBetSlipId"];
			calculatedCashoutAmount: Parameters<
				CashoutStore["setMinThreshold"]
			>[0];
			maxGain: Parameters<CashoutStore["setMinThreshold"]>[1];
			gainBonus: Parameters<CashoutStore["setMaxThreshold"]>[1];
			betSlipSecureWinSetting?: BetSlipSecureWinSetting | null;
		},
		isEventPage: boolean = false
	) {
		ModalService.onOpen();

		if (isEventPage) {
			this.isEventAutosellModalOpen = true;
		} else {
			this.isAutosellModalOpen = true;
		}

		this._currentCashoutValue = bet.calculatedCashoutAmount;
		this.openedAutosellModalBetSlipId = bet.id;

		this.setMinThreshold(bet.calculatedCashoutAmount, bet.maxGain);
		this.setMaxThreshold(bet.maxGain, bet.gainBonus);

		if (
			this.minThreshold &&
			this.maxThreshold &&
			Number(this.minThreshold) >= Number(this.maxThreshold)
		) {
			this.isAutocashoutUnavailable = true;
			this.isSaveBtnDisabled = true;
			this.isAutosellFormValid = false;
		} else {
			if (bet && bet.betSlipSecureWinSetting) {
				this.isConfigurationExists = true;
				this.secureWinAmount =
					bet.betSlipSecureWinSetting.threshold.toFixed(2);
			} else {
				this.isConfigurationExists = false;
				this.secureWinAmount = "";
			}
		}
	}

	@action.bound
	openAutosellBetDetails(bet: {
		id: CashoutStore["openedAutosellModalBetSlipId"];
		calculatedCashoutAmount: Parameters<CashoutStore["setMinThreshold"]>[0];
		maxGain: Parameters<CashoutStore["setMinThreshold"]>[1];
		gainBonus: Parameters<CashoutStore["setMaxThreshold"]>[1];
		betSlipSecureWinSetting?: BetSlipSecureWinSetting | null;
	}) {
		this._currentCashoutValue = bet.calculatedCashoutAmount;
		this.openedAutosellModalBetSlipId = bet.id;

		this.setMinThreshold(bet.calculatedCashoutAmount, bet.maxGain);
		this.setMaxThreshold(bet.maxGain, bet.gainBonus);

		if (
			this.minThreshold &&
			this.maxThreshold &&
			Number(this.minThreshold) >= Number(this.maxThreshold)
		) {
			this.isAutocashoutUnavailable = true;
			this.isSaveBtnDisabled = true;
			this.isAutosellFormValid = false;
		} else {
			if (bet && bet.betSlipSecureWinSetting) {
				this.isConfigurationExists = true;
				this.secureWinAmount =
					bet.betSlipSecureWinSetting.threshold.toFixed(2);
			} else {
				this.isConfigurationExists = false;
				this.secureWinAmount = "";
			}
		}
	}

	@action.bound
	closeAutosellModalBetDetails() {
		this.minThreshold = null;
		this.maxThreshold = null;
		this.secureWinAmount = null;
		this._currentCashoutValue = null;
		this.openedAutosellModalBetSlipId = null;
		this.isAutocashoutUnavailable = false;
		this.isSaveBtnDisabled = true;
		this.isAutosellFormValid = true;
		this.isMinThresholdValid = true;
		this.isMaxThresholdValid = true;
		this.isAutosellModalCashedOut = false;
		this.autosellModalCashoutGain = null;
		this.isConfigurationExists = false;
	}

	@action.bound
	closeAutosellModal() {
		this.isAutosellModalOpen = false;
		this.isEventAutosellModalOpen = false;
		this.minThreshold = null;
		this.maxThreshold = null;
		this.secureWinAmount = null;
		this._currentCashoutValue = null;
		this.openedAutosellModalBetSlipId = null;
		this.isAutocashoutUnavailable = false;
		this.isSaveBtnDisabled = true;
		this.isAutosellFormValid = true;
		this.isMinThresholdValid = true;
		this.isMaxThresholdValid = true;
		this.isAutosellModalCashedOut = false;
		this.autosellModalCashoutGain = null;
		this.isConfigurationExists = false;
	}

	@action.bound
	async setSecureWinConfiguration(
		id: string,
		threshold: number
	): Promise<void> {
		try {
			const response =
				await AutocashoutApiService.setSecureWinConfiguration(
					id,
					threshold,
					createApiRequestOptions(
						RequestContextType.Wallet,
						this.parentStore.walletId
					)
				);

			if (
				response &&
				response.betSlipSecureWinSetting &&
				response.betSlipSecureWinSetting.threshold
			) {
				const betSlip = this.parentStore.betsList.find(
					(b) => b.id === id
				);

				if (betSlip) {
					betSlip.betSlipSecureWinSetting =
						response.betSlipSecureWinSetting;
					betSlip.isAutoCashoutSet = true;
				}

				if (
					this.parentStore.betsPerEvent != null &&
					this.parentStore.betsPerEvent.length !== 0
				) {
					this.parentStore.betsPerEvent.forEach((eventBets) => {
						const betPerEvent = eventBets.bets.find(
							(b) => b.id === id
						);
						if (betPerEvent != null) {
							betPerEvent.betSlipSecureWinSetting =
								response.betSlipSecureWinSetting;
							betPerEvent.isAutoCashoutSet = true;
						}
					});
				}

				this.parentStore.rootStore.notificationStore.success(
					localizationService.t(
						"mybets:CASHOUT.SECURE_WIN_SET_SUCCESS_MESSAGE"
					)
				);
			}

			if (
				response &&
				!response.betSlipSecureWinSetting &&
				response.betSlipSecureWinSettingValidation
			) {
				switch (response.betSlipSecureWinSettingValidation.errorCode) {
					case 123:
						this.isAutocashoutUnavailable = true;
						break;

					// @ts-expect-error
					case (181, 144):
						this.minThreshold =
							response.betSlipSecureWinSettingValidation.minThreshold.toFixed(
								2
							);
						this.maxThreshold =
							response.betSlipSecureWinSettingValidation.maxThreshold.toFixed(
								2
							);

						this.validateThreshold();
						break;
				}
			}
		} catch (error) {
			if (
				error.data?.betSlipSecureWinSettingValidation?.errorCode ===
				BetSlipErrorCodesEnum.WalletActivationGracePeriod
			) {
				this.parentStore.rootStore.walletGracePeriodPopupStore.setMessage(
					localizationService.t(
						"mybets:CASHOUT.GRACE_PERIOD_EXPIRED_ACTIONS"
					) as string
				);
				this.parentStore.rootStore.walletGracePeriodPopupStore.showPopup();
			} else if (error.data.errorCode === 400241) {
				this.parentStore.rootStore.notificationStore.error(
					localizationService.t(
						"mybets:CASHOUT.AUTO_CASHOUT_UNAVAILABLE"
					)
				);
			} else if (
				error.data?.betSlipSecureWinSettingValidation?.errorCode ==
				400210
			) {
				this.parentStore.rootStore.notificationStore.error(
					localizationService.t(
						"mybets:CASHOUT.AUTO_CASHOUT_UNAVAILABLE"
					)
				);
			} else {
				this.parentStore.rootStore.notificationStore.error(
					localizationService.t(
						"mybets:CASHOUT.SECURE_WIN_SET_ERROR_MESSAGE"
					)
				);
			}
		} finally {
			this.closeAutosellModal();
			this.closeAutosellModalBetDetails();
			if (this.parentStore instanceof MyBetsStore) {
				if (this.parentStore.betDetailsModalStore.isStoreInitialized) {
					this.parentStore.betDetailsModalStore.onAutoSellClose();
				}
			}
		}
	}

	@action.bound
	async removeSecureWinConfiguration(id: string) {
		try {
			await AutocashoutApiService.removeSecureWinConfiguration(
				id,
				createApiRequestOptions(
					RequestContextType.Wallet,
					this.parentStore.walletId
				)
			);

			const betSlip = this.parentStore.betsList.find((b) => b.id === id);

			if (betSlip) {
				betSlip.betSlipSecureWinSetting = null;
				betSlip.isAutoCashoutSet = false;
			}

			if (
				this.parentStore.betsPerEvent != null &&
				this.parentStore.betsPerEvent.length !== 0
			) {
				this.parentStore.betsPerEvent.forEach((eventBets) => {
					const betPerEvent = eventBets.bets.find((b) => b.id === id);
					if (betPerEvent != null) {
						betPerEvent.betSlipSecureWinSetting = null;
						betPerEvent.isAutoCashoutSet = false;
					}
				});
			}

			this.parentStore.rootStore.notificationStore.success(
				localizationService.t(
					"mybets:CASHOUT.SECURE_WIN_DELETE_SUCCESS_MESSAGE"
				)
			);
		} catch (error) {
			if (error.data.errorCode === 400241) {
				this.parentStore.rootStore.notificationStore.error(
					localizationService.t(
						"mybets:CASHOUT.AUTO_CASHOUT_UNAVAILABLE"
					)
				);
			} else {
				this.parentStore.rootStore.notificationStore.error(
					localizationService.t(
						"mybets:CASHOUT.SECURE_WIN_DELETE_ERROR_MESSAGE"
					)
				);
			}
		} finally {
			this.closeAutosellModal();
			this.closeAutosellModalBetDetails();
			if (this.parentStore instanceof MyBetsStore) {
				if (this.parentStore.betDetailsModalStore.isStoreInitialized) {
					this.parentStore.betDetailsModalStore.onAutoSellClose();
				}
			}
		}
	}

	@action.bound
	handleChange(inputValue: string) {
		if (checkDecimals(inputValue)) {
			this.secureWinAmount = inputValue;
			this.validateThreshold();
		}
	}

	@action.bound
	updateAutoSellValues(newCashoutValue: number, maxGain: number) {
		this._currentCashoutValue = newCashoutValue;

		this.setMinThreshold(newCashoutValue, maxGain);

		if (this.secureWinAmount) {
			this.validateThreshold();
		}
	}

	@action.bound
	validateThreshold() {
		const secureWinAmount = Number(this.secureWinAmount);
		const minThreshold = Number(this.minThreshold);
		const maxThreshold = Number(this.maxThreshold);

		if (secureWinAmount < minThreshold) {
			this.isMinThresholdValid = false;
		} else {
			this.isMinThresholdValid = true;
		}

		if (secureWinAmount > maxThreshold) {
			this.isMaxThresholdValid = false;
		} else {
			this.isMaxThresholdValid = true;
		}

		if (this.secureWinAmount) {
			this.isAutosellFormValid =
				this.isMinThresholdValid && this.isMaxThresholdValid;

			this.isSaveBtnDisabled = !this.isAutosellFormValid;
		} else {
			this.isSaveBtnDisabled = true;
		}
	}

	@action.bound
	setMinThreshold(cashoutValue: number, maxGain: number) {
		let increaseValue =
			(maxGain - cashoutValue) *
			this._minSecureWinThresholdIncreasePercentage;

		if (increaseValue > 2) {
			increaseValue = 2;

			this.minThreshold = Math.round(
				cashoutValue + increaseValue
			).toFixed(2);
		} else {
			this.minThreshold = (
				Math.round((cashoutValue + increaseValue) * 10) / 10
			).toFixed(2);
		}
	}

	@action.bound
	setMaxThreshold(maxGain: number, gainBonus: number) {
		let maxGainTmp = maxGain;

		if (gainBonus) {
			maxGainTmp -= gainBonus;
		}

		this.maxThreshold = (
			Math.round(maxGainTmp * 1000) / 1000 -
			0.01
		).toFixed(2);
	}

	//#endregion  auto sell

	private isCashedOut(update: {
		isPaidOut?: boolean | null;
		cType?: CashoutType | null;
		cashoutGain?: number | null;
	}) {
		return (
			update.isPaidOut &&
			update.cType === CashoutType.JACKTIME &&
			update.cashoutGain !== undefined &&
			(update.cashoutGain || 0) > 0
		);
	}
}
